import Remote from '@/remote/Remote';
import User from '@/types/User';
import Project, {ProjectDef, ProjectFile, ProjectId} from '@/types/Project';
import SearchFilter, {SearchOrder} from '@/types/SearchFilter';
import Submission, {SubmissionDef, SubmissionId} from '@/types/Submission';

class RemoteMock extends Remote {
    private projects: {[key: string /* ProjectId */]: Project} = {};
    private files: {[key: string /* ProjectId */]: ProjectFile[]} = {};
    private submissions: {[key: string /* SubmissionId */]: Submission} = {};
    private submissionBinaries: {[key: string /* SubmissionId */]: Uint8Array} = {};
    private favorites: {[key: string /* User Id */]: SubmissionId[]} = {};
    private uid: string = '';
    private projectCounter : number = 0;

    //
    // users
    //

    public async login(): Promise<User> {
        const email = 'fake@fake.com';
        const uid = await this.createFakeUser(email);
        this.favorites[uid] = [];
        return { uid, email, displayName: 'Fake user', username: 'fake' };
    }

    public async logout(): Promise<void> {
        return Promise.resolve(undefined);
    }

    // noinspection JSUnusedLocalSymbols
    public async createFakeUser(email: string): Promise<UID> {
        this.uid = Math.random().toString(36).substring(7);
        this.favorites[this.uid] = [];
        return this.uid;
    }

    public async deleteFakeUser(): Promise<void> {
        delete this.favorites[this.uid];
    }

    //
    // project list
    //

    // noinspection JSUnusedLocalSymbols
    public async userProjectList(uid: UID): Promise<ProjectDef[]> {
        return Object.entries(this.projects).map(((value) => ({ id: value[0], project: value[1] })));
    }

    //
    // project
    //

    public async createProject(projectInfo: Project): Promise<ProjectId> {
        const id = `abcde${this.projectCounter++}`;
        if (id in this.projects) {
            throw new Error('There is already a project with this ID.');
        }
        this.projects[id] = { ...projectInfo };
        this.files[id] = [];
        return Promise.resolve(id);
    }

    public async deleteProject(projectId: string): Promise<void> {
        delete this.projects[projectId];
        delete this.files[projectId];
    }

    public async loadProject(projectId: ProjectId): Promise<Project> {
        if (!(projectId in this.projects)) {
            throw new Error('Document does not exist');
        }
        return this.projects[projectId];
    }

    public async updateProject(project: ProjectDef): Promise<void> {
        this.projects[project.id] = { ...this.projects[project.id], ...project.project };
    }

    //
    // files
    //

    public async saveFile(uid: UID, projectId: string, file: ProjectFile): Promise<void> {
        await this.deleteFiles(uid, projectId, [file.name]);
        const content = Array.isArray(file.content) ? new Uint8Array(file.content) : file.content;
        this.files[projectId].push({ name: file.name, content, dirty: false});
        return Promise.resolve();
    }

    public async updateFiles(uid: UID, projectId: ProjectId, filesToUpdate: ProjectFile[]): Promise<void[]> {
        return Promise.all(filesToUpdate.map(async (f) => this.saveFile(uid, projectId, f)));
    }

    public async listFiles(uid: UID, projectId: ProjectId): Promise<string[]> {
        if (projectId in this.files)
            return this.files[projectId].map((f) => f.name);
        else
            return [];
    }

    public async deleteFiles(uid: UID, projectId: string, filenames: string[]): Promise<void[]> {
        if (projectId in this.files)
            this.files[projectId] = this.files[projectId].filter((file: ProjectFile) => !filenames.includes(file.name));
        return Promise.resolve([]);
    }

    public async deleteAllFiles(uid: UID, projectId: ProjectId): Promise<void[]> {
        this.files[projectId] = [];
        return Promise.resolve([]);
    }

    public async loadAllFiles(uid: UID, projectId: ProjectId): Promise<ProjectFile[]> {
        return this.files[projectId];
    }

    //
    // project submission
    //

    public async submitProject(submission: Submission, binaryFile: FileContent, previousSubmissionId?: SubmissionId): Promise<SubmissionId> {
        const id = previousSubmissionId || `submission${this.projectCounter++}`;
        let votes = 0;
        if (previousSubmissionId) {
           votes = this.submissions[previousSubmissionId!].votes;
        }
        this.submissions[id] = { ...submission, votes };
        this.submissionBinaries[id] = binaryFile as Uint8Array;
        return id;
    }

    public async loadSubmission(submissionId: SubmissionId): Promise<[Submission, FileContent]> {
        if (!(submissionId in this.submissions)) {
            throw new Error('Submission not found');
        }
        return [this.submissions[submissionId], this.submissionBinaries[submissionId]];
    }

    public async searchSubmissions(filter: SearchFilter): Promise<SubmissionDef[]> {
        if (filter.submissionId) {
            if (this.submissions[filter.submissionId!]) {
                return [{id: filter.submissionId!, submission: this.submissions[filter.submissionId!]} as SubmissionDef];
            } else {
                return [];
            }
        }
        let submissions: SubmissionDef[] = Object.entries(this.submissions).map(([id, submission]) => ({ id, submission } as SubmissionDef));
        if (filter.partialName) {
            submissions = submissions.filter((s) => s.submission.name.toLowerCase().includes(filter.partialName!.toLowerCase()));
        }
        if (filter.genre) {
            submissions = submissions.filter((s) => s.submission.genres.includes(filter.genre!));
        }
        if (filter.onlyFavoritesUid) {
            submissions = submissions.filter((s) => this.favorites[filter.onlyFavoritesUid!].includes(s.id));
        }
        switch (filter.order) {
            case SearchOrder.New:
                submissions.sort((a, b) => b.submission.date - a.submission.date);
                break;
            case SearchOrder.Favorites:
                submissions.sort((a, b) => b.submission.votes - a.submission.votes);
                break;
        }
        return submissions;
    }

    public async deleteSubmission(submissionId: SubmissionId): Promise<void> {
        for (const uid of Object.keys(this.favorites)) {
            this.favorites[uid] = this.favorites[uid].filter((value) => value !== submissionId);
        }
        if (this.submissions[submissionId]) {
            const projectId = this.submissions[submissionId].projectId;
            delete this.submissions[submissionId];
            delete this.submissionBinaries[submissionId];
            const project = this.projects[projectId];
            if (project) {
                delete project.submissionId;
            }
        }
    }

    public async upvoteSubmission(uid: UID, submissionId: SubmissionId): Promise<void> {
        if (this.favorites[uid].includes(submissionId)) {
            return;
        }
        ++this.submissions[submissionId].votes;
        this.favorites[uid].push(submissionId);
    }

    public async downvoteSubmission(uid: UID, submissionId: SubmissionId): Promise<void> {
        if (!this.favorites[uid].includes(submissionId)) {
            return;
        }
        --this.submissions[submissionId].votes;
        this.favorites[uid] = this.favorites[uid].filter((value) => value !== submissionId);
    }

    public async listFavorites(uid: UID): Promise<SubmissionId[]> {
        if (uid in this.favorites)
            return this.favorites[uid];
        return [];
    }

    //
    // other
    //

    public async close(): Promise<void> {
        return Promise.resolve();
    }

    public debugState(): void {
        console.log({
            projects: this.projects,
            submissions: this.submissions,
            favorites: this.favorites,
            files: this.files,
            submissionBinaries: this.submissionBinaries,
        });
    }

    public async ensureEmptyDatabase(): Promise<void> {
        if (Object.keys(this.projects).length !== 0 ||
            Object.keys(this.submissions).length !== 0 ||
            Object.keys(this.favorites).length !== 0 ||
            Object.keys(this.submissionBinaries).length !== 0 ||
            Object.keys(this.files).length !== 0) {
            this.debugState();
            throw new Error('Database is not empty.');
        }
    }
}

export default RemoteMock;
